import Papa from 'papaparse';

// Normalize an input row into { name, territory? }.
// Accepts loose column names so the same code handles CSV exports
// with headers like "Account Name", "Account", "Name" + optional
// "Territory", "Region", "Owner Territory".
const NAME_KEYS = ['account name', 'account_name', 'account', 'name', 'agency', 'department', 'organization', 'org'];
const TERRITORY_KEYS = ['territory', 'fy27 territory', 'owner territory', 'region', 'segment'];

function pickField(row, candidates) {
  if (!row || typeof row !== 'object') return null;
  const lower = {};
  for (const k of Object.keys(row)) lower[k.trim().toLowerCase()] = row[k];
  for (const c of candidates) {
    const v = lower[c];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return null;
}

function normalizeRows(rows) {
  return rows
    .map(row => {
      const name = pickField(row, NAME_KEYS);
      if (!name) return null;
      const territory = pickField(row, TERRITORY_KEYS);
      return { name, territory };
    })
    .filter(Boolean);
}

// Plain text: one entry per line. Supports tab or comma separators
// for an optional second-column territory. Skips empty lines and a
// header line whose first cell matches a known name key.
export function parsePastedText(text) {
  if (!text) return [];
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length === 0) return [];

  // Detect header
  const firstParts = lines[0].split(/[\t,]/).map(s => s.trim().toLowerCase());
  const hasHeader = NAME_KEYS.includes(firstParts[0]);
  const dataLines = hasHeader ? lines.slice(1) : lines;

  return dataLines.map(line => {
    const parts = line.split(/[\t,]/).map(s => s.trim()).filter((s, i) => i < 2 || s !== '');
    const name = parts[0];
    const territory = parts[1] || null;
    if (!name) return null;
    return { name, territory };
  }).filter(Boolean);
}

export function parseCsvFile(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: ({ data }) => {
        try {
          resolve(normalizeRows(data));
        } catch (e) {
          reject(e);
        }
      },
      error: reject,
    });
  });
}

export async function parseXlsxFile(file) {
  // xlsx is ~350KB minified — loaded on demand at first import, not in main
  const XLSX = await import('xlsx');
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  if (!sheet) return [];
  const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
  return normalizeRows(rows);
}

export async function parseSpreadsheetFile(file) {
  const name = (file.name || '').toLowerCase();
  if (name.endsWith('.csv') || file.type === 'text/csv') {
    return parseCsvFile(file);
  }
  if (name.endsWith('.xlsx') || name.endsWith('.xls') ||
      file.type?.includes('spreadsheet') || file.type?.includes('excel')) {
    return parseXlsxFile(file);
  }
  throw new Error(`Unsupported spreadsheet format: ${file.name}`);
}
